import React, { useState, useEffect } from "react"
import gql from "graphql-tag"
import { graphql } from '@apollo/client/react/hoc'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Input, FormGroup, Row, Col, Alert, Progress } from "reactstrap"
import moment from "moment"
import { UncontrolledTooltip } from "reactstrap"
import UpdatePaymentScheduleInfo from "./UpdatePaymentScheduleInfo"
import { PreferredDateTimeContext } from "../../Context/PreferredDateTimeContext"
import Label from "reactstrap/lib/Label"
import { HasPermissionsQuery } from "../../Functions/querys"
import { CreatePaymentScheduleMutation, UpdatePaymentScheduleMutation } from "../Transactions/Mutations"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import DateTimePicker from "../../Material/DateTimePicker"
import { PaymentScheduleQuery } from "../../Drivers/Driver/Detail/Queries"
import withApolloClient from '../../withApolloClient'
import "./PaymentScheduleModal.css"
import { PAYMENT_SCHEDULE_STATUS_COLOR_MAP } from "../../Constants"
import DatetimeRenderer from "../../Material/DatetimeRenderer"

const DriverDetailQuery = gql`query Driver($id: ID!){
    driver(id: $id){
        id
        pk
        balance
        totalScheduledAmount
        balanceWriteOff
        currentAgreement {
            id
            type {
                id
                chargeType
            }
            upcomingRentalTask {
                amountToCharge
                scheduleTime
            }
        }
    }
}`

const PaymentScheduleModal = ({ action, paymentSchedule, driverId, paymentScheduleId, handleClose, open, apolloClient, hasPermissions, refetchQuery, refetchPaymentScheduleQuery, driver, timezone }) => {
    const [showEdit, setShowEdit] = useState("")
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(null)
    const [loading, setLoading] = useState(false)
    const [paymentScheduleCanceled, setPaymentScheduleCanceled] = useState(false)
    const [input, setInput] = useState({
        amountToCharge: "",
        scheduleDatetime: "",
        notes: ""
    })
    const [createAnother, setCreateAnother] = useState(false)

    useEffect(() => {
        if (open && action === "CREATE") {
            setInput({
                amountToCharge: "",
                scheduleDatetime: "",
                notes: ""
            })
            setError(null)
            setSuccess(null)
        }
    }, [open, action])

    const toggleEdit = (field) => {
        setShowEdit(showEdit === field ? "" : field)
    }

    const isFormValid = () => {
        return input.amountToCharge > 0 && input.scheduleDatetime
    }

    const createPaymentSchedule = (e) => {
        if (!isFormValid()) {
            setError("Please fill all required fields")
            return
        }

        try {
            setLoading(true)
            setError(null)
            setSuccess(null)

            const mutationInput = {
                ...input,
                driverId: driverId
            }

            apolloClient.mutate({
                mutation: CreatePaymentScheduleMutation,
                variables: { input: mutationInput },
            }).then((result) => {
                if (result?.data?.createPaymentSchedule?.ok) {
                    setSuccess("Payment schedule created successfully!")
                    if (createAnother) {
                        setInput({
                            scheduleDatetime: input.scheduleDatetime,
                            amountToCharge: "",
                            notes: ""
                        })
                        setCreateAnother(false)
                    } else {
                        refetchQuery()
                        handleClose("")
                    }
                    setLoading(false)
                } else {
                    setError(result?.data?.createPaymentSchedule?.errors?.[0]?.messages || "An error has occurred. Please contact Admin!")
                }
                setLoading(false)
            }).catch((error) => {
                setError(error.message)
                setLoading(false)
            })
        } catch (error) {
            setError(error.message)
            setLoading(false)
        }
    }

    const deletePaymentSchedule = () => {
        try {
            setLoading(true)
            apolloClient.mutate({
                mutation: UpdatePaymentScheduleMutation,
                variables: { input: { "status": "Canceled", "paymentScheduleId": paymentScheduleId } },
            }).then((result) => {
                if (result?.data?.updatePaymentSchedule?.ok) {
                    setPaymentScheduleCanceled(true)
                    setError(null)
                    setSuccess("Payment schedule canceled successfully!")
                } else {
                    setError(result?.data?.updatePaymentSchedule?.errors?.[0]?.messages || "An error occurred")
                }
                setLoading(false)
            }).catch((err) => {
                setError("An error has occurred " + err)
                setLoading(false)
            })
        } catch (err) {
            setError("An error has occurred " + err)
            setLoading(false)
        }
    }

    const updateInput = (e) => {
        const { name, value, type } = e.target
        setInput(prev => ({
            ...prev,
            [name]: type === "number" ? (parseFloat(value) || 0) : value
        }))
    }

    const closeConfirmationPopup = () => {
        handleClose("")
        refetchQuery()
    }

    const handleCreateAnotherChange = () => {
        setCreateAnother(!createAnother)
        if (!createAnother) {
            setSuccess(null)
        }
    }

    let driverBalance = (driver && driver.balance) || 0
    let driverUpcomingCharge = driver?.currentAgreement?.upcomingRentalTask?.amountToCharge || 0
    let driverBadDebt = Math.abs(driver?.balanceWriteOff) || 0

    let availableAmount = parseFloat(driverBalance)
    let totalScheduledAmount = (driver?.totalScheduledAmount) || 0
    availableAmount -= parseFloat(totalScheduledAmount)
    availableAmount += parseFloat(driverUpcomingCharge)
    availableAmount += parseFloat(driverBadDebt)

    return (
        <Modal isOpen={open} toggle={handleClose} className="payment-schedule-modal">
            <ModalHeader>{action} Payment Schedule</ModalHeader>
            <ModalBody>
                <Row>
                    {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
                    {success && <Col xs={12}><Alert color="success">{success}</Alert></Col>}
                    {loading && <Progress animated color="info" value={100} />}

                    {(driverUpcomingCharge !== 0 || totalScheduledAmount !== 0) && (
                        <Col>
                            <Label for="currentBalance">Current Balance</Label>
                            <p className="column-content">{driverBalance}</p>
                        </Col>
                    )}

                    {driverUpcomingCharge > 0 && (
                        <Col>
                            <Label for="upcomingCharge">Upcoming Charge</Label>
                            <p className="column-content">{driverUpcomingCharge}</p>
                        </Col>
                    )}

                    {driverBadDebt > 0 && (
                        <Col>
                            <Label for="badDebt">Bad Debt</Label>
                            <p className="column-content">{driverBadDebt}</p>
                        </Col>
                    )}

                    {totalScheduledAmount > 0 && (
                        <Col>
                            <Label>Already Scheduled Amount</Label>
                            <p className="column-content">{totalScheduledAmount}</p>
                        </Col>
                    )}

                    <Col>
                        <Label for="upcomingCharge">Available Amount</Label>
                        <p className="column-content">{availableAmount}</p>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <FormGroup>
                            {showEdit === "amountToCharge" && (
                                <UpdatePaymentScheduleInfo
                                    open={showEdit === "amountToCharge"}
                                    target="amountToCharge"
                                    title="Amount to charge"
                                    type="number"
                                    name="amountToCharge"
                                    value={paymentSchedule?.amountToCharge}
                                    paymentScheduleId={paymentScheduleId}
                                    refetchQuery={refetchPaymentScheduleQuery}
                                    handleClose={() => toggleEdit("")}
                                />
                            )}
                            <Label for="amountToCharge">Enter Amount to Charge</Label>
                            {action === "UPDATE" && paymentSchedule && ["PENDING", "SCHEDULED"].includes(paymentSchedule.status) && (
                                <i id="amountToCharge" className="fa fa-pencil-square-o float-right" onClick={() => toggleEdit("amountToCharge")} />
                            )}
                            <Input
                                type="number"
                                name="amountToCharge"
                                id="amountToCharge"
                                min={0}
                                onChange={updateInput}
                                value={action === "CREATE" ? input.amountToCharge : (paymentSchedule?.amountToCharge || "")}
                                placeholder="Enter Amount to Charge"
                                disabled={action === "UPDATE"}
                            />
                        </FormGroup>
                    </Col>

                    <Col xs={12}>
                        <FormGroup>
                            {showEdit === "scheduleDatetime" && (
                                <UpdatePaymentScheduleInfo
                                    open={showEdit === "scheduleDatetime"}
                                    target="scheduleDatetime"
                                    title="Schedule Date Time"
                                    type="datetime-local"
                                    name="scheduleDatetime"
                                    value={paymentSchedule?.scheduleDatetime}
                                    paymentScheduleId={paymentScheduleId}
                                    refetchQuery={refetchPaymentScheduleQuery}
                                    handleClose={() => toggleEdit("")}
                                    driverId={driverId}
                                />
                            )}
                            <Label for="scheduleDatetime">Select Date & Time to Charge</Label>
                            {action === "UPDATE" && paymentSchedule && ["PENDING", "SCHEDULED"].includes(paymentSchedule.status) && (
                                <i id="scheduleDatetime" className="fa fa-pencil-square-o float-right" onClick={() => toggleEdit("scheduleDatetime")} />
                            )}

                            {action === "CREATE" ? (
                                <PreferredDateTimeContext.Consumer>
                                    {({ preferredDateTimeInfo }) => (
                                        <>
                                            <DateTimePicker
                                                type="datetime-local"
                                                min={moment().tz(preferredDateTimeInfo.timezone).format(moment.HTML5_FMT.DATETIME_LOCAL)}
                                                max={moment().tz(preferredDateTimeInfo.timezone).add(5, 'day').format(moment.HTML5_FMT.DATETIME_LOCAL)}
                                                name="scheduleDatetime"
                                                id="scheduleDatetime"
                                                value={input.scheduleDatetime || ""}
                                                placeholder="Select schedule datetime"
                                                setError={setError}
                                                updateInput={(name, value) => setInput(prev => ({ ...prev, [name]: value }))}
                                                driverId={driverId}
                                            />
                                        </>
                                    )}
                                </PreferredDateTimeContext.Consumer>
                            ) : (
                                <PreferredDateTimeContext.Consumer>
                                    {({ preferredDateTimeInfo }) => (
                                        <p>
                                            <DatetimeRenderer
                                                datetime={paymentSchedule?.scheduleDatetime}
                                                driverId={driverId}
                                            />
                                        </p>
                                    )}
                                </PreferredDateTimeContext.Consumer>
                            )}
                        </FormGroup>
                    </Col>

                    <Col xs={12}>
                        <FormGroup>
                            {showEdit === "notes" && (
                                <UpdatePaymentScheduleInfo
                                    open={showEdit === "notes"}
                                    target="notes"
                                    title="Notes"
                                    type="text"
                                    name="notes"
                                    value={paymentSchedule?.notes}
                                    paymentScheduleId={paymentScheduleId}
                                    refetchQuery={refetchPaymentScheduleQuery}
                                    handleClose={() => toggleEdit("")}
                                />
                            )}
                            <Label for="notes">Payment schedule notes</Label>
                            {action === "UPDATE" && paymentSchedule && ["PENDING", "SCHEDULED"].includes(paymentSchedule.status) && (
                                <i id="notes" className="fa fa-pencil-square-o float-right" onClick={() => toggleEdit("notes")} />
                            )}
                            <Input
                                type="text"
                                name="notes"
                                id="notes"
                                onChange={updateInput}
                                placeholder="Notes"
                                disabled={action === "UPDATE"}
                                value={action === "CREATE" ? input.notes : (paymentSchedule?.notes || "")}
                            />
                        </FormGroup>
                    </Col>

                    {paymentSchedule?.status && (
                        <>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="status">Status</Label>
                                    <p className="column-content">
                                        <Badge color={PAYMENT_SCHEDULE_STATUS_COLOR_MAP[paymentSchedule.status] || 'primary'} style={{ fontSize: 10, padding: 5 }}>
                                            {paymentSchedule.status}
                                        </Badge>
                                    </p>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                {["PENDING", "SCHEDULED"].includes(paymentSchedule.status) ? (
                                    <>
                                        <Label>Created By</Label>
                                        <p>
                                            {paymentSchedule.createdBy?.firstName
                                                ? `${paymentSchedule.createdBy.firstName} ${paymentSchedule.createdBy.lastName}`
                                                : paymentSchedule.createdBy?.username || "--"}
                                        </p>
                                    </>
                                ) : paymentSchedule.status === "CANCELED" && (
                                    <>
                                        <Label>Canceled By</Label>
                                        <p>
                                            {paymentSchedule.canceledBy?.firstName
                                                ? `${paymentSchedule.canceledBy.firstName} ${paymentSchedule.canceledBy.lastName}`
                                                : paymentSchedule.canceledBy?.username || "--"}
                                        </p>
                                    </>
                                )}
                            </Col>
                        </>
                    )}
                </Row>
            </ModalBody>

            <ModalFooter>
                {action === "CREATE" && (
                    <Col className="d-flex justify-content-start align-items-center">
                        <Label check className="m-0 mr-2">
                            <Input
                                type="checkbox"
                                checked={createAnother}
                                onChange={handleCreateAnotherChange}
                            />
                            Create another?
                        </Label>
                    </Col>
                )}

                <Col className="d-flex justify-content-end">
                    {paymentSchedule?.status && ["PENDING", "SCHEDULED"].includes(paymentSchedule.status) && (
                        <>
                            {showEdit === "cancelPaymentSchedule" && (
                                <ConfirmationPopup
                                    open={showEdit === "cancelPaymentSchedule"}
                                    title="Cancel Payment Schedule"
                                    loading={loading}
                                    message="Are you sure you want to cancel this payment schedule?"
                                    handleClose={() => paymentScheduleCanceled ? closeConfirmationPopup() : toggleEdit("")}
                                    action="Cancel Payment Schedule"
                                    confirmAction={deletePaymentSchedule}
                                    color="danger"
                                    error={error || ""}
                                    success={paymentScheduleCanceled ? "Scheduled Payment canceled successfully" : ""}
                                />
                            )}
                            <Button
                                className="payment-schedule-modal-danger-btn float-left"
                                id="deletePaymentSchedule"
                                onClick={() => toggleEdit("cancelPaymentSchedule")}
                                disabled={!(hasPermissions?.includes("delete_paymentschedule")) || paymentScheduleCanceled}
                            >
                                CANCEL
                            </Button>
                            {!hasPermissions?.includes("delete_paymentschedule") && (
                                <UncontrolledTooltip target="deletePaymentSchedule">
                                    Don't have enough permission to perform this action
                                </UncontrolledTooltip>
                            )}
                        </>
                    )}

                    {action === "CREATE" && (
                        <Button
                            className="payment-schedule-modal-primary-btn float-right"
                            color="primary"
                            onClick={createPaymentSchedule}
                            disabled={loading || !isFormValid()}
                        >
                            {loading ? "Loading..." : "Schedule Payment"}
                        </Button>
                    )}
                    &nbsp;&nbsp;
                    <Button
                        className="payment-schedule-modal-secondary-btn float-right"
                        color="secondary"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Col>
            </ModalFooter>
        </Modal>
    )
}

export default graphql(PaymentScheduleQuery, {
    skip: ({ paymentScheduleId }) => !paymentScheduleId,
    options: ({ paymentScheduleId }) => ({ variables: { id: paymentScheduleId }, fetchPolicy: 'cache-first' }),
    props: ({ data: { paymentSchedule, loading, refetch, variables } }) => ({
        paymentSchedule,
        loading,
        variables,
        refetchPaymentScheduleQuery: () => {
            return refetch({
                query: PaymentScheduleQuery,
                variables: {
                    ...variables,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    return { paymentSchedule: fetchMoreResult.paymentSchedule }
                },
            })
        },
    }),
})(
    graphql(HasPermissionsQuery, {
        options: () => ({ variables: { userPermissions: ["delete_paymentschedule"] }, fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }),
        props: ({ data: { hasPermissions, variables } }) => ({
            hasPermissions,
            variables,
        }),
    })(
        graphql(DriverDetailQuery, {
            skip: ({ driverId }) => !driverId,
            options: ({ driverId }) => ({ variables: { id: driverId }, fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }),
            props({ data: { driver, loading, refetch, variables } }) {
                return {
                    driver,
                    loading,
                    variables,
                    refetchDriverQuery: () => {
                        return refetch({
                            query: DriverDetailQuery,
                            variables: {
                                ...variables,
                            },
                            updateQuery: (previousResult, { fetchMoreResult }) => {
                                return { driver: fetchMoreResult.driver }
                            },
                        })
                    },
                }
            },
        })(withApolloClient(PaymentScheduleModal))
    )
)
